import { Dispatch, ReactNode } from "react"
import { BuilderBlock } from "sharedTypes/builder"
import { BadgeTemplate } from "sharedTypes/badge"
import { EventUserRole } from "services/permissions"
import { Boolean as StringBoolean } from "utilities/enums"

export enum ModalName {
  Placeholder,
  EnrichDisabled,
  ExportGuests,
  ExportInvitationList,
  ImportGuests,
  NewEmail,
  NewGuest,
  NewPlusOnes,
  GuestListForm,
  SegmentForm,
  SendTestEmail,
  SwitchAccount,
  TableForm,
  UpdateGuestStatus,
  Upgrade,
  WebPageURL,
  EventLink,
  PublicEventTitle,
  AccountUserForm,
  SelectGuest,
  FormForm,
  QuestionForm,
  AddFormQuestion,
  NewTextMessage,
  ScheduleSendEmail,
  EventActivityForm,
  EditRecipients,
  PreviewTextMessage,
  EditTextRecipients,
  EditEmailName,
  EditTextMessageName,
  ExportTextReport,
  InviteExhibitorOrAssignLicense,
  UpgradeSuccessful,
  BuyTextMessageCreditsModal,
  PaymentSuccessful,
  ViewGuestLinks,
  CloneEvent,
  NewBadgeTemplate,
  BadgePrinterSetup,
  BadgeTemplateBrowser,
  RenameBadgeTemplate,
  BadgeTemplateSaveValidationWarning,
  EditBadgeTemplateDimensions,
  EditBadgeTemplatePrinterAndMedia,
  HeroTemplate,
  WhatsAppSendingOptions,
  GuestCommunicationPreferences,
  InviteExhibitorToPurchase,
  GoogleAnalytics,
  EventCapacity,
}

export type CurrentModal = {
  name: ModalName
  params?: { id: string }
}

export type GlobalState = {
  currentAccount: Account;
}

export type Context = GlobalState & {
  dispatch: Dispatch<{ type, data }>;
}

export type User = {
  id: string;
  authenticationToken: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  photoUrl: string;
  accounts: Account[];
  confirmed: boolean;
  supportLogin: boolean;
  leadRetrievalUser: boolean;
}

export type StripeAccount = {
  id: number;
  accountId: string;
  stripeId: string;
  businessName: string | null;
}

export type Account = {
  id: string
  stripeCustomer: boolean
  name: string
  admin: boolean
  canCreateEvents: boolean
  owner: boolean
  industry: string
  logoUrl: string
  subscription: Subscription | null
  customDomains: CustomDomain[]
  emailEnabled: boolean
  dailyEmailLimit: number
  dailyTextLimit: number
  featureFlags: { [x: string]: boolean }
  stripeAccounts: StripeAccount[]
  disabled: boolean
  smsCredits: number
}

export type CustomDomain = {
  id: number;
  domain: string;
  dkim: { error: string | null, valid: boolean };
  dkim2: { error: string | null, valid: boolean };
  dmarc: { error: string | null, valid: boolean };
  spf: { error: string | null, valid: boolean };
  lastTestedAt: string | null;
  verifiedAt: string | null;
  verifyTxtKey: string | null;
  legacyAuthenticated: boolean | null;
  validSigning: boolean;
  mailbox: string;
}

export type ApiKey = {
  id: string;
  label: string | null;
  tokenLastFour: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  // Only present in the response right after creation - never re-fetchable.
  plaintextToken: string | null;
}

export enum SubscriptionStatuses {
  Active = "active",
  PastDue = "past_due"
}

export enum SubscriptionType {
  Free = "Free",
  Basic = "Basic",
  Professional = "Professional",
  Corporate = "Corporate",
  Enterprise = "Enterprise",
}

export type Subscription = {
  type: SubscriptionType;
  cancelAtPeriodEnd: boolean;
  amount: number;
  interval: "month" | "year";
  licenses: number;
  periodStartsAt: string;
  periodEndsAt: string;
  stripeId: string | null;
  expired: boolean;
  legacy: boolean;
  status: SubscriptionStatuses;
  hostedInvoiceUrl?: string | null;
}

export type EventCounts = {
  guests: number
  confirmedGuests: number
  waitlistedGuests: number
  declinedGuests: number
  unconfirmedGuests: number
  arrivedGuests: number
  unarrivedGuests: number
  seatedGuests: number
  unseatedGuests: number
  tables: number
  seats: number
  assignedSeats: number
  unassignedSeats: number
  waitlistedSeats: number
  standingGuests?: number
  checkInsByLocation?: {
    checkInLocationId: number | null
    guests: number
    firstAt: string
    lastAt: string
  }[]
  grossSales: number
  netSales: number
  ticketOrders: number
  ticketedGuests: number
  tickets: number
  emails: number
  openedEmails: number
  hardBouncedEmails: number
  softBouncedEmails: number
  bouncedEmails: number
  clickedEmails: number
  dailyEmails: number
  dailyTexts: number
  eventGroups: {id: number, size: number}[]
  forms: number
  badges?: number
  questions: number
  responses: number
  formSubmissions: number
  formSubmissionLineItems: {
    registration?: number
    rsvp?: number
    ticket?: number
    waitlist?: number
    survey?: number
  }
  texts: number
  unassignedGuests: number
}

export type EventEntryTarget = "site" | "portal" | "form" | "none"

export type EventUrls = {
  root: string
  site: string
  sitePreview: string
  portal: string
  entry: string
}

export type GuestUrls = {
  page: string
  qr: string
  email: string
  forms: Record<string, string>
}

export type DioboxEvent = {
  id: string
  addressLine1: string
  addressLine2: string
  checkInLocations: CheckInLocation[]
  city: string
  country: string
  counts: EventCounts
  currency: string
  customDomain: CustomDomain|null
  deferredTicketAssignmentEnabled: boolean
  emailNotificationsCustomMessage: string|null
  publicEventTitle: string
  emailNotificationsReplyTo: string
  emailNotificationsSenderMailbox: string|null
  emailNotificationsSenderName: string
  emailNotificationsUserIds: string[]
  endDate: string|null
  eventGroups: EventGroup[]
  segments: Segment[]
  users: EventUser[]
  guestCount: number
  maxTeamSize: number
  maxTables: number|null
  nonTicketedGuestLimit: number|null
  capacity: number|null
  agendaEnabled: boolean
  checkInLocationsEnabled: boolean
  privateEmailsEnabled: boolean
  publicEmailsEnabled: boolean
  enhanceEnabled: boolean
  startDate: string
  state: string
  tables: Table[]
  timezone: string
  title: string
  venue: string
  website: Website
  zipcode: string
  currentUserRole: EventUserRole
  whiteLabel: boolean
  notificationsEnabled: boolean
  badgePrintingEnabled: boolean
  segmentsEnabled: boolean
  qrCodesEnabled: boolean
  kioskModeEnabled: boolean
  expiringQrCodes: boolean
  guestGroupsEnabled: boolean
  ccEmailsEnabled: boolean
  sendConfirmationEmails: boolean
  translations: Translations
  portalBackgroundColor: string
  portalDateTimeLocation: string
  portalEventImageUrl: string|null
  portalEventVideoUrl: string|null
  portalLogoUrl: string|null
  portalMapUrl: string
  portalPrimaryColor: string
  portalShowEventImage: boolean
  portalShowEventVideo: boolean
  portalShowLogo: boolean
  portalShowMap: boolean
  portalTopNavBackgroundColor: string
  portalTopNavTextColor: string
  externalId: string
  portalEventImageScale: number
  portalPageHeader: string
  portalShowPageHeader: boolean
  portalCaptchaEnabled: boolean
  includePassbook: boolean
  includeQrCode: boolean
  stripeAccountId: string | null
  leadRetrievalLicenseFee: string
  leadRetrievalEnabled: boolean
  textsEnabled: boolean
  passBuilderEnabled: boolean
  customLanguageTemplatesEnabled: boolean
  zapierEnabled: boolean
  slug: string
  slugClaimed: boolean
  slugChangedAt: string | null
  slugRenameAllowedAt: string | null
  rootTarget: EventEntryTarget | null
  rootFormId: number | null
  vanityUrlsEnabled: boolean
  urls?: EventUrls
  googleAnalyticsMeasurementId: string | null
  attachIcs: boolean
  language: string
  portalShowEventTitle: boolean
  kioskAccentColor: string | null
  kioskLogoUrl: string | null
  kioskBackgroundImageUrl: string | null
  kioskShowEventTitle: boolean
  kioskShowGuestPhoto: boolean
  kioskShowGuestSeat: boolean
  kioskScanQrCodeEnabled: boolean
  kioskWalkInRegistrationEnabled: boolean
  /** When set, the form used for kiosk walk-in registration (ids match API / DB). */
  kioskWalkInRegistrationForm: { id: number; externalId: string } | null
  useLegacyIosBadges: boolean
  autoOptInEnabled: boolean
  smsFallbackEnabled: boolean
  defaultOptInTextId: number | null
  defaultLanguageTemplateId: string | null
  walletPassData?: WalletPassData
  checklistTicks?: Record<string, boolean>
  // {} until someone customizes the dashboard.
  dashboardLayout?: DashboardLayout | Record<string, never>
}

export type DashboardLayout = { order: string[], hidden: string[] }

export type WalletPassData = {
  backgroundColor?: string
  textColor?: string
  labelColor?: string
  logoText?: string
  squareLogoUrl?: string
  horizontalLogoUrl?: string
  appleBannerUrl?: string
  googleHeroUrl?: string
  headerLabel?: string
  headerValueSource?: string
  headerStaticValue?: string
  primaryLabel?: string
  primaryValueSource?: string
  primaryStaticValue?: string
  primaryMovedBelowBanner?: boolean
  secondaryLabel?: string
  secondaryValueSource?: string
  secondaryStaticValue?: string
  auxiliary1Label?: string
  auxiliary1ValueSource?: string
  auxiliary1StaticValue?: string
  auxiliary2Label?: string
  auxiliary2ValueSource?: string
  auxiliary2StaticValue?: string
  guestNameEnabled?: boolean
  guestNameLabel?: string
  qrCodeEnabled?: boolean
  backSections?: {
    id?: string
    type?: string
    label?: string
    value?: string
  }[]
}

export type PublicDioboxEvent = {
  attachIcs: boolean
  guestsCanUpdateProfile: boolean
  translations: {
    addressLine1: string
    addressLine2: string
    buttonNotWorking: string
    city: string
    company: string
    confirmed: string
    continue: string
    country: string
    declined: string
    edit: string
    editProfile: string
    editProfileHeader: string
    edited: string
    emailAddress: string
    emailNotifications: string
    emailSubscription: string
    eventActivity: string
    firstName: string
    fullName: string
    guest: string
    lastName: string
    payment: string
    phone: string
    plusOnesHeader: string
    postalCode: string
    qrCodeForCheckIn: string
    receipt: string
    registerGuestsNow: string
    capacityReachedMessage: string
    registrationClosingMessage: string
    registrationEditMessage: string
    registrationSubmitButton: string
    registrationSuccessMessage: string
    rsvpClosingMessage: string
    rsvpConfirm: string
    rsvpDecline: string
    rsvpEditMessage: string
    rsvpPlusOneLabel: string
    rsvpPlusOneNo: string
    rsvpPlusOneYes: string
    rsvpSubmitButton: string
    rsvpSuccessMessage: string
    save: string
    submitted: string
    surveyClosingMessage: string
    surveyEditMessage: string
    surveySubmitButton: string
    surveySuccessMessage: string
    state: string
    ticketClosingMessage: string
    ticketEditMessage: string
    ticketSubmitButton: string
    ticketSuccessMessage: string
    title: string
    unconfirmed: string
    uploadPhoto: string
    viewMap: string
    whatsappOptInDisclaimer: string
    waitlistClosingMessage: string
    waitlistEditMessage: string
    waitlistSubmitButton: string
    waitlistSuccessMessage: string
    waitlisted: string
    yourInformation: string
    yourInformationDescription: string
  }
  accountName: string
  forms: {
    id: string
    name: string
    description: string | null
    price: string
    currency: string
    limitPerSubmission: number
    variablePrice: boolean
    formData: {
      blocks: {
        type: string
        data: {
          id: string
          title: string
          choices?: string[]
          type: string
          required: boolean
        }
      }[]
    }
    type: string
    closed: boolean
    profileAddressEnabled: boolean
    profileAddressRequired: boolean
    profileCityEnabled: boolean
    profileCityRequired: boolean
    profileCompanyEnabled: boolean
    profileCompanyRequired: boolean
    profileCountryEnabled: boolean
    profileCountryRequired: boolean
    profilePhoneEnabled: boolean
    profilePhoneRequired: boolean
    profilePostalCodeEnabled: boolean
    profilePostalCodeRequired: boolean
    profileStateEnabled: boolean
    profileStateRequired: boolean
    profileTitleEnabled: boolean
    profileTitleRequired: boolean
    profileNameEnabled: boolean
    profileEmailEnabled: boolean
  }[]
  stripeAccountId: string | null
  whiteLabel: boolean
  language: string
  leadRetrievalLicenseFee: string
  id: string
  deferredAssignmentEnabled: boolean
  name: string
  ics: string
  backgroundColor: string
  captchaEnabled: boolean
  dateTimeLocation: string
  eventImageUrl: string
  eventImageScale: number
  logoUrl: string
  mapUrl: string
  pageHeader: string
  primaryColor: string
  showEventImage: boolean
  showLogo: boolean
  showMap: boolean
  showPageHeader: boolean
  topNavBackgroundColor: string
  topNavTextColor: string
}

export type CheckLeadRetrievalUserEmailResponse = {
  existingLicense: boolean
  user: {
    email: string
    id: string
    firstName: string
    lastName: string
  }
}

export type LeadRetrievalUser = {
  id?: string
  email?: string
  password?: string
  name?: string
  eventId?: string
}

export type CreatePaymentResponse = {
  url: string
}

export type LeadRetrievalLicensesSummary = {
  total: number
  used: number
  remaining: number
  assigned: { id: string, email: string, assignedAt: string, inviteToken: string }[]
  active: { id: number, email: string, activatedAt: string }[]
  purchaseInvites: { id: string, email: string, invitedAt: string }[]
}

export type LeadScans = {
  hot: number
  warm: number
  cold: number
}

export type LeadRetrievalLicenseTokenResponse = {
  alreadyRedeemed: boolean
  email?: string
  user?: {
    id: string
    email: string
    firstName: string
    lastName: string
  } | null
}

export type Translations = {
  addressLine1: string
  addressLine2: string
  city: string
  company: string
  confirmed: string
  continue: string
  country: string
  declined: string
  editProfile: string
  editProfileHeader: string
  emailAddress: string
  emailNotifications: string
  emailSubscription: string
  firstName: string
  fullName: string
  lastName: string
  payment: string
  phone: string
  postalCode: string
  qrCodeForCheckIn: string
  registerGuestsNow: string
  capacityReachedMessage: string
  registrationClosingMessage: string
  registrationEditMessage: string
  registrationSubmitButton: string
  registrationSuccessMessage: string
  rsvpClosingMessage: string
  rsvpConfirm: string
  rsvpDecline: string
  rsvpEditMessage: string
  rsvpPlusOneLabel: string
  rsvpPlusOneNo: string
  rsvpPlusOneYes: string
  rsvpSubmitButton: string
  rsvpSuccessMessage: string
  save: string
  state: string
  ticketClosingMessage: string
  ticketEditMessage: string
  ticketSubmitButton: string
  ticketSuccessMessage: string
  title: string
  unconfirmed: string
  uploadPhoto: string
  viewMap: string
  whatsappOptInDisclaimer: string
  waitlistClosingMessage: string
  waitlistEditMessage: string
  waitlistSubmitButton: string
  waitlistSuccessMessage: string
  waitlisted: string
  yourInformation: string
  yourInformationDescription: string
  eventActivity: string
  surveySubmitButton: string
  surveySuccessMessage: string
  surveyEditMessage: string
  surveyClosingMessage: string
  edit: string
  edited: string
  guest: string
  plusOnesHeader: string
  submitted: string
  receipt: string
  buttonNotWorking: string
  birthdate: string
  temporalQrCodeForCheckIn: string
  temporalQrCodeMessage: string
  guestRestrictionErrorMessage: string
  kioskAccessRestricted: string
  kioskCheckedIn: string
  kioskDuplicateQrCode: string
  kioskInvalidQrCode: string
  kioskPrintingBadge: string
  kioskPrintingError: string
  kioskRegister: string
  kioskScanQrCode: string
}

export type EventUser = {
  id: number
  email: string
  role: EventUserRole
  owner: boolean
  user: User
  event: Partial<DioboxEvent>
  confirmed: boolean
  photoUrl: string
  name: string
  licenseSource?: "organizer_activation" | "exhibitor_purchase"
}

export enum TableShapes {
  Circle = "circle",
  Row = "row",
  HalfRound = "half-round",
  Square = "square",
  Rectangle = "rectangle",
  Oval = "oval",
  Serpentine = "serpentine",
}

export type Table = {
  id: number;
  externalId: string;
  url?: string;
  name: string;
  notes: string | null;
  topSize: number;
  bottomSize: number;
  rightSize: number;
  leftSize: number;
  seats: Seat[];
  shape: TableShapes;
  waitlistedGuestIds: string[];
  waitlistedGuests: GuestSummary[];
}

export type Seat = {
  number: number;
  guestId: string | null;
  reserved: boolean;
  guest: GuestSummary | null;
  description: string | null;
}

export enum QuestionTypes {
  SingleAnswer = "Single Answer",
  MultipleAnswer = "Multiple Answer",
  DropdownMenu = "Dropdown Menu",
  Terms = "Terms",
  SingleLineText = "Single Line Text",
  ParagraphText = "Paragraph Text",
  FileUpload = "File Upload",
  TextBlock = "Text Block",
}

export type FormQuestion = {
  // `id` is the stable external_id from QuestionSerializer — safe to use
  // directly as the form-question filter field key without a separate
  // externalId field (confirmed from QuestionSerializer: `attribute :id`
  // returns `object.external_id`).
  id: string;
  title: string;
  choices: string[];
  type: QuestionTypes;
  required: boolean;
  responses: {
    [key: string]: number
  }
  isPlaceholder: boolean
}

export type EventQuestionResponse = {
  guestId: string
  name: string
  email: string
  photoUrl: string
  response: string
}
export enum WebsiteActionBehavior {
  RegisterForEvent = "tickets",
  ApplyToAttend = "waitlist",
}

export type Website = {
  content: string;
  coverImageUrl: string;
  dateEnabled: boolean;
  dateHeader: string;
  dateLine1: string;
  dateLine2: string;
  links: string[];
  linksEnabled: boolean;
  locationEnabled: boolean;
  locationVenue: string;
  locationLine1: string;
  locationLine2: string;
  locationLine3: string;
  latitude?: string;
  longitude?: string;
  mapEnabled: boolean;
  published: boolean;
  previewToken: string;
  templateId: number | null;
  title: string;
  urlPath: string;
  url?: string;
  previewUrl?: string;
  viewsCount: number;
  speakers: Speaker[];
  speakersEnabled?: boolean;
  speakersHeader?: string;
  agendaRows: AgendaRows;
  agendaEnabled?: boolean;
  agendaHeader?: string;
  rsvpHeaderEventLogoUrl: string;
  rsvpHeaderTitle: string;
  rsvpHeaderLine1: string;
  rsvpHeaderLine2: string;
  rsvpProfileAddressEnabled: boolean;
  rsvpProfileAddressRequired: boolean;
  rsvpProfileBirthDateEnabled: boolean;
  rsvpProfileBirthDateRequired: boolean;
  rsvpProfileCityEnabled: boolean;
  rsvpProfileCityRequired: boolean;
  rsvpProfileCompanyEnabled: boolean;
  rsvpProfileCompanyRequired: boolean;
  rsvpProfileCountryEnabled: boolean;
  rsvpProfileCountryRequired: boolean;
  rsvpProfilePhoneEnabled: boolean;
  rsvpProfilePhoneRequired: boolean;
  rsvpProfilePostalCodeEnabled: boolean;
  rsvpProfilePostalCodeRequired: boolean;
  rsvpProfileStateEnabled: boolean;
  rsvpProfileStateRequired: boolean;
  rsvpProfileTitleEnabled: boolean;
  rsvpProfileTitleRequired: boolean;
  rsvpButtonColor: string;
  actionEnabled: boolean;
  actionBehavior: WebsiteActionBehavior;
  actionText: string;
  websiteData: {
    blocks: BuilderBlock[];
  }
}

export type AgendaRows = AgendaRow[]

export type AgendaRow = (AgendaHeading & AgendaItem) & {type: string;}

export type AgendaHeading = {
  id: number;
  title: string;
  description: string;
  startsAt: string;
}

export type AgendaItem = {
  id: number;
  title: string;
  description: string;
  speakerIds: number[];
  startsAt: string;
  endsAt: string;
}

export type Speaker = {
  id: number;
  name: string;
  company: string;
  title: string;
  description: string;
  link: string;
  photoUrl: string;
  position: number;
};

export type EventGroup = {
  id: number;
  icon: string | null;
  color?: string | null;
  title: string;
  notifications: Notification[];
}

export type Email = {
  id: number
  eventId: string
  emailData: { blocks: BuilderBlock[] }
  from: string
  fromMailbox: string | null
  replyTo: string
  sentFromEmail: string
  opened: number
  sendTo: {}
  sendToUniqueEmails: boolean
  sentOn: string | null
  sendScheduledAt: string | null
  sentSize: number
  subject: string
  title: string
  emailAttachment: { fileName: string | null; fileSize: number | null; url: string; }
  showEmailAttachment: boolean
  showUnsubscribe: boolean
  createdAt: string
  updatedAt: string
  backgroundColor: string
  borderColor: string
  hasForm: boolean
  bodyColor: string
  bounced: number
  softBounced: number
  hardBounced: number
  clicks: number
  complained: number
  eventTitle: string
  noResponse: number
  responseCount: number
  responses: number
  sent: number
  unopened: number
}

export enum GuestStatus {
  Unconfirmed = 0,
  Confirmed = 1,
  Declined = 2,
  Waitlisted = 3,
}

export type SeatingAssignment = {
  guestId: string;
  id: number;
  seat: number;
  seatingTableId: number;
  seatingTableTitle: string;
  shape: string;
} | null

export type WaitlistTable = {
  id: number;
  name: string;
} | null

export type Notification = {
  email: boolean;
  sms: boolean;
  id: number;
  userId: string;
}

export type GuestEmail = {
  id: number
  emailId: number
  title: string
}

export type GuestSummary = {
  id: string
  name: string
  checkedIn: number
  status: GuestStatus
  photoExists: boolean
  photoUrl: string
  personInfo: PersonInfo
  seatingAssignment: SeatingAssignment
  waitlistTable: WaitlistTable
  standing: boolean
  groupSize: number
  plusOnes?: GuestSummary[]
  guestId?: string
  externalId: string
  confirmedTime?: string
  createdTime: string
  updatedAt: string
  emails?: GuestEmail[]
  eventGroups: EventGroup[]
}

export type FormSubmissionLineItemResponse = {
  question: string, response: string | string[]
}

export type FormSubmissionLineItem = {
  externalId: string
  url?: string
  createdAt: string
  currency: string
  formType: FormType
  id: string
  name: string
  price: string
  formSubmission: {
    eventId: string
    externalId: string
    url?: string
    paymentMethod: string
    paymentDetails: string
  }
  guestId: string
  responses: FormSubmissionLineItemResponse[]
}

type TextActivityItemData = {
  to: string
  sid: string
  body: string
  from: string
  status: string
  textId: number
  startDate: string
  date: string
}

export type TextActivityItem = {
  id: number
  textId: string
  title: string
  eventId: string
  eventTitle: string
  guestId: string
  personId: string
  transactionDate: string
  type: string
  displayType: string
  details: any
  userId: string
  createdAt: string
  updatedAt: string
  date: string
  data: TextActivityItemData
}

export type PersonInfo = {
  averageRating: number
  birthDate: string
  companyName: string
  companyTitle: string
  customFields: CustomField[]
  email: string|null
  emailHardBounced: boolean
  emailSoftBounced: boolean
  firstName: string
  gender: string
  id: string
  lastName: string
  noShows: number
  notes: string
  photoUrl: string
  totalAttended: number
  totalConfirmed: number
  totalEvents: number
  totalSpent: number
  transactions: Transaction[]
  unsubscribed: boolean
  optedInWhatsapp: boolean
  formSubmissionLineItems: FormSubmissionLineItem[]
  phone?: string
  city?: string
  country?: string
  address?: Address
}

export type Guest = {
  id: string
  externalId: string
  urls?: GuestUrls
  checkedIn: number
  eventGroups: EventGroup[]
  eventId: string
  genderUnknownGuests: number
  girlGuests: number
  guyGuests: number
  notifications: Notification[]
  seatingAssignment: SeatingAssignment
  status: GuestStatus
  personInfo: PersonInfo
  waitlistTable: WaitlistTable
  standing: boolean
  group: GuestSummary[]
  questionsAndAnswers: []
  confirmedTime?: string
  createdTime: string
  updatedAt: string
}

export enum CustomImportGuestHeaderType {
  EmailField = "EmailField",
  PhoneField = "PhoneField",
  TextField = "TextField",
}

export type ImportGuestsHeader = {
  key: string;
  value: string;
  custom?: boolean;
  type?: CustomImportGuestHeaderType;
}

export type Transaction = {
  id: number;
  type: string;
  displayType: string;
  title: string|null;
  eventId: string;
  guestId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  transactionDate: string;
  data?: any;
  details?: string;
}

export type EmailData = {
  opened: string;
  clicked: string;
  bounced: "true" | "false";
  rsvp: "yes" | "no";
}

export type EmailTransaction = Transaction & {
  emailId: number
  data: EmailData
  clicks: number
  fromName: string
  fromEmail: string
  subject: string
  opens: number
  softBounced: boolean
  hardBounced: boolean
  toEmail: string
}

export type CheckInTransaction = Transaction & {
  checkInLocationId: number | null;
  guestOfGuest: boolean;
  checkInTime: string;
}

export type ActivityItem = (
  Transaction | EmailTransaction | CheckInTransaction | FormSubmissionLineItem | TextActivityItem
) & {
  id: string
  type: "AttendanceTransaction" | "EmailTransaction" | "NoteTransaction" | "FormSubmission" | "TextTransaction";
  displayType: string;
  title: string;
  date: string;
}

export type Address = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export type CustomField = {
  type: string;
  title: string;
  value: Address | string | {};
  id?: number;
  _destroy?: boolean;
  deleted?: boolean;
  default: boolean|null;
}

export type CheckInLocation = {
  id: number;
  name: string;
  icon: string;
}

export type Plan = {
  type: SubscriptionType
  stripePriceIds: { month: string, year: string }
  monthlyPrice: number
  annualPrice: number
  eventLimit: number | null
  guestLimit: number | null
  teamLimit: number
  tableLimit: number | null
  checkInAlerts: boolean
  checkInLocations: boolean
  qrCodes: boolean
  expiringQrCodes: boolean
  enrichGuests: boolean
  standardEmails: boolean
  privateEmails: boolean
  whiteLabelEmails: boolean
  speakerProfiles: boolean
  eventAgenda: boolean
  realTimeVacancy: boolean
  shareTablesExternally: boolean
  kioskMode: boolean
  vanityUrls: boolean
  badgePrinting: boolean
  rsvpForms: boolean
  waitlistForms: boolean
  leadRetrieval: boolean
  texts: boolean
  passBuilder: boolean
  customLanguageTemplates: boolean
  zapier: boolean
  segments: boolean
  ticketForms: boolean
  downgradeText: string
  downgradeConfirmations: string[]
  upgradeText: string
  upgradeFeatures: [{icon: string, text: string}]
  features: string[]
  downgradeOnly: boolean
}

export const ItemTypes = {
  GUEST: "guest",
}

export enum SegmentRuleField {
  CheckInDate = "Check-In Date",
  CheckInStatus = "Check-In Status",
  Confirmation = "Confirmation",
  EmailCampaign = "Email Campaign",
  GuestList = "Guest List",
  Seating = "Seating",
  Email = "Email",
  CreatedDate = "Created Date",
  // Wire value mirrors `confirmed_time` on the API. Added alongside a BE
  // change to `SegmentViewer` + `segment_rule.json` schema.
  LastConfirmedDate = "Last Confirmed Date",
  EmailsSent = "Emails Sent",
  PlusOnesCount = "Plus-Ones Count",
  EmailSubscription = "Email Subscription",
  WhatsAppOptIn = "WhatsApp Opt-In",
  // Wire values must match segment_viewer.rb dispatch: "Company" / "Job Title"
  // map via `.underscore.tr(' ', '_')` → filter_by_company / filter_by_job_title.
  Company = "Company",
  JobTitle = "Job Title",
  FormSubmitted = "Form Submitted",
  FormNotSubmitted = "Form Not Submitted",
}

export type SegmentRuleOperator = "eq" | "ne" | "gt" | "lt" | "in" | "not_in" | "has_all" | "not_has_all" | "contains" | "not_contains"

// Rule value union covering every shape the backend accepts:
//   - string     — enum fields with scalar operators (eq/ne) + date ISO strings
//   - string[]   — enum fields with `in` / `not_in`
//   - number     — numeric fields (Plus-Ones Count, Emails Sent) with scalar ops
//   - number[]   — numeric fields with `in` / `not_in` (not exposed in UI yet)
// Mixed arrays aren't valid for any single field but the type allows them
// because `Array.map` returning a uniform type is hard to express in TS
// when the coercion function returns a union. The FE coerces string →
// number at the chip→rule boundary for numeric fields (see
// GuestFilters.chipToRule); chip rendering coerces the other way.
export type SegmentRuleValue = string | number | (string | number)[]

export type Segment = {
  id: string,
  name: string,
  color: string,
  rules: {
    // Widened to `string` to accommodate dynamic form-question fields whose
    // wire key is `"Form Question:<externalId>"` — a value outside the
    // SegmentRuleField enum. Static fields still use enum values; the union
    // accepts both.
    field: SegmentRuleField | string,
    operator: SegmentRuleOperator,
    value: SegmentRuleValue,
  }[],
  count: number,
}

// Ad-hoc chip filter for the columns view. Same field/operator vocabulary as
// Segment.rules so a chip set round-trips losslessly to "Save as segment".
// `values` is always an array (forward-compat with multi-value chips later);
// v1 ships single-value chips, so values[0] is the rule on the wire.
export type Filter = {
  id: string,
  // `string` rather than `SegmentRuleField` to accommodate form-question
  // filter chips whose field is `"Form Question:<externalId>"`.
  field: SegmentRuleField | string,
  operator: SegmentRuleOperator,
  values: string[],
}

export enum GuestFilterType {
  All = "All",
  EventGroup = "Guest List",
  DefaultSegment = "Default Segment",
  CustomSegment = "Custom Segment",
}

export type GuestFilter = {
  type: GuestFilterType;
  groupId?: string | number;
  segmentId?: string;
  icon?: string;
  iconColor?: string;
  label: string;
  count: number | null;
}

export type SegmentSize = {
  id: string
  size: number | null
}

type RegistrationType = "registration" | "rsvp" | "ticket" | "waitlist" | "survey"

export type EventActivity = {
  interval: string
  timezone: string
  registrations: ({ period: string } & Record<RegistrationType, number>)[]
  sales: { period: string, gross: number, net: number }[]
  checkIns: { period: string, count: number }[]
  checkInsByLocation?: { period: string, checkInLocationId: number | null, count: number }[]
}

export type EventActivityParams = { interval: string, from?: string, to?: string }

export type EventPageViews = {
  webPage: { date: string, views: number }[]
  portalAndForms?: { date: string, views: number }[]
  portalAndFormsTotal?: number
}

export type EventPageViewsParams = { from?: string, to?: string }

export type ImportData = {
  rowCount: number;
  headers: boolean;
  examples?: (string | undefined)[][];
  importFileId?: number | null;
}

export type APIError = {
  userMessage: string
}

export type PreviewInvoice = {
  prorationAmount: number | null;
  discountAmount: number | null;
  unusedTimeAmount: number | null;
  startingBalance: number | null;
  amountDue: number | null;
  prorationDate: number | null;
}

export type PromoCode = {
  id: string;
  name: string;
  amountOff: number;
  percentOff: number;
}

export type AccountUser = {
  id: number;
  email: string;
  user: User
  owner: boolean;
  admin: boolean;
  canCreateEvents: boolean;
  events: EventUser[];
  createdAt: string;
}

export type MessageTemplate = {
  id: string
  icon: string
  name: string
  features: string[]
  formType?: FormType
}

export type MessageTemplateGroup = {
  description: string;
  name: string;
  templates: MessageTemplate[];
  requiredPlan: SubscriptionType;
}

export type SmsCreditPackages = {
  [key: string]: number
}

export type WhatsAppTemplateVariableType =
  | "locked" | "user" | "media"
  // Legacy types — kept for transition until all backend templates are migrated
  | "editable" | "selectable"

export type WhatsAppMediaType = "image" | "document"

export type WhatsAppTemplateVariable = {
  label: string
  type: WhatsAppTemplateVariableType
  mergeTag?: string
  source?: string
  defaultValue?: string
  mediaType?: WhatsAppMediaType
}

export type WhatsAppTemplateChannel = "whatsapp" | "sms"

export type WhatsAppTemplate = {
  contentSid: string
  name: string
  description: string
  preview: string
  variables: WhatsAppTemplateVariable[]
  category: "utility" | "marketing"
  messageType: string
  language: string
  channels: WhatsAppTemplateChannel[]
  buttons?: string[]
}

export type AccountConfiguration = {
    emailTemplates: { groups: MessageTemplateGroup[] }
    plans: Plan[]
    translations: Translations
    smsCreditPackages: SmsCreditPackages
    badgeTemplates: BadgeTemplate[]
    whatsappTemplates: WhatsAppTemplate[]
}

export type LanguageTemplate = {
  id: string
  name: string
  isOffered: boolean
  translations: Translations
  createdAt: string
  updatedAt: string
}

export enum FormType {
  Registration = "Registration",
  Ticket = "Ticket",
  RSVP = "RSVP",
  Waitlist = "Waitlist",
  Survey = "Survey",
}

export enum TextMessageType {
  Invitation = "Invite Text",
  Confirmation = "Confirmation Text",
  General = "General Text",
}

export type BuilderBlockWithBlockId = {
  block: BuilderBlock,
  blockId: number,
}

export enum GuestStrategy {
  Create = "create",
  Upsert = "upsert",
  Restrict = "restrict",
}

export type Form = {
  id: number
  externalId: string
  url?: string
  name: string
  private: boolean
  price: string
  currency: string
  submissionsEndAt: string
  limitPerSubmission: number
  disabled: boolean
  spotsLeft: number | null
  variablePrice: boolean
  grossSales: string
  netSales: string
  type: FormType
  submissionLineItemsCount: number
  quantity?: number
  description?: string
  formData: {
    blocks: BuilderBlock[]
  }
  profilePhoneEnabled: boolean
  profilePhoneRequired: boolean
  profileCompanyEnabled: boolean
  profileCompanyRequired: boolean
  profileAddressEnabled: boolean
  profileAddressRequired: boolean
  profileCityEnabled: boolean
  profileCityRequired: boolean
  profileCountryEnabled: boolean
  profileCountryRequired: boolean
  profilePostalCodeEnabled: boolean
  profilePostalCodeRequired: boolean
  profileStateEnabled: boolean
  profileStateRequired: boolean
  profileTitleEnabled: boolean
  profileTitleRequired: boolean
  translations: Translations
  languageTemplateId: string | null
  profileNameEnabled: boolean
  profileEmailEnabled: boolean
  plusOnesEnabled: boolean
  maxPlusOnes: number
  plusOneNameEnabled: boolean
  plusOneEmailEnabled: boolean
  plusOneEmailRequired: boolean
  submissionMessage: string | null
  submitButtonLabel: string | null
  profileBirthdateEnabled: boolean
  profileBirthdateRequired: boolean
  profileWhatsappOptInEnabled?: boolean
  guestStrategy: GuestStrategy
  confirmationEmailsDisabled: StringBoolean
}

export type PaginationMeta = {
  currentPage: number;
  nextPage: number;
  prevPage: number;
  totalCount: number;
  totalPages: number;
}

export type FormFormValues = {
  private: Boolean | StringBoolean
  limitPerSubmission?: number
  disabled?: Boolean | StringBoolean
  name?: string
  submissionsEndAt?: string
  type: FormType
  description?: string
  quantity?: number
  confirmationEmailsDisabled: boolean | StringBoolean
  languageTemplateId?: string
}

export type Submission = {
  createdAt: string
  currency: string
  externalId: string
  url?: string
  price: string
  submitterEmail: string
  submitterName: string
  lineItems: SubmissionLineItem[]
  paymentMethod: string
  paymentDetails: string
}

export type SubmissionSummary = Pick<
  Submission, "createdAt" | "currency" | "externalId" | "price" | "submitterEmail" | "submitterName"
> & {
  lineItems: { form: Pick<SubmissionForm, "name" | "type">, guest: Pick<SubmissionGuest, "status"> | null }[]
}

export type SubmissionGuest = {
  companyName?: string
  companyTitle?: string
  email: string
  firstName: string
  id: string
  lastName: string
  name: string
  phone?: string
  photoUrl: string
  qrCodeUrl: string
  status: GuestStatus
  birthdate: string
  address: {
    city?: string
    country?: string
    line1?: string
    line2?: string
    state?: string
    zip?: string
  }
  expiringQrCodes: boolean
  externalId: string
  urls?: GuestUrls
}

export type SubmissionForm = {
  name: string
  type: FormType
  formData: { blocks: BuilderBlock[]}
  profileAddressEnabled: boolean
  profileAddressRequired: boolean
  profileCityEnabled: boolean
  profileCityRequired: boolean
  profileCompanyEnabled: boolean
  profileCompanyRequired: boolean
  profileCountryEnabled: boolean
  profileCountryRequired: boolean
  profilePhoneEnabled: boolean
  profilePhoneRequired: boolean
  profilePostalCodeEnabled: boolean
  profilePostalCodeRequired: boolean
  profileStateEnabled: boolean
  profileStateRequired: boolean
  profileTitleEnabled: boolean
  profileTitleRequired: boolean
  profileNameEnabled: boolean
  profileEmailEnabled: boolean
  profileZipEnabled: boolean
  profileBirthdateEnabled: boolean
  profileBirthdateRequired: boolean
  profileWhatsappOptInEnabled?: boolean
}

export type SubmissionLineItem = {
  currency: string
  externalId: string
  url?: string
  form: SubmissionForm,
  guest: SubmissionGuest,
  name: string,
  price: string,
  responses: { [key: string]: string | [] }
  createdAt: string
  updatedAt: string
  plusOnes: { id: string; name: string; email: string; url?: string }[]
  formSubmission: {
    eventId: string,
    externalId: string,
    paymentDetails: string,
    paymentMethod: string,
  }
}

export enum FixedBlockType {
  GuestName,
  Registration,
  Payment,
  PlusOnes,
  FormSubmission,
}

export type RegistrationField = {
  name: string,
  enabled: boolean,
  required: boolean,
  fieldName: string,
  readOnly?: boolean,
  select?: boolean,
  hideRequired?: boolean,
  requiredLocked?: boolean,
}

export type RegistrationFields = {
  personal: RegistrationField[],
  address: RegistrationField[],
}

export enum FormsSubpath {
  StyleAndConfiguration = "style-and-configuration",
}

export enum StyleAndConfigurationSubpath {
  Overview = "overview",
  GuestActions = "guest-actions",
  GuestConfirmations = "guest-confirmations",
  AutomatedEmails = "automated-emails",
  PortalSetup = "portal-setup",
  Confirmations = "confirmations",
  EmailResponders = "email-responders",
}

export enum SubmissionsSubpath {
  Questions = "questions",
}

export enum FormStatus {
  Published,
  Ended,
  Disabled,
  GatewayNotConnected,
  SoldOut,
  EventFull,
}

export type TabButton<T> = {
  id: string,
  title: string,
  iconName: string
  subpath?: T,
}

export type FormFormType = {
  name: string;
  type: FormType;
  icon: string;
  component: ReactNode
};

export enum ButtonBlockAction {
  Form = "form",
  Website = "website",
  Other = "other",
}

export enum FormSubmissionSortOption {
  CreatedAtAsc = "created_at_asc",
  CreatedAtDesc = "created_at_desc",
  GuestNameAsc = "guest_name_asc",
  GuestNameDesc = "guest_name_desc",
  PriceAsc = "price_asc",
  PriceDesc = "price_desc",
}

export type RadioOption = {
  label: string | ReactNode
  value: string
}

export enum DomainStatusValue {
  LegacyAuthenticated = "legacy authenticated",
  Authenticated = "authenticated",
  Pending = "pending",
  NotAuthenticated = "not authenticated",
}

type SubmissionResponseObject = {
  url: string
  file: string
  type: string
};

export type SubmissionResponseType = string | SubmissionResponseObject | string[]

export type TextMessageDeliveryCounts = {
  delivered: number
  deliveryUnknown: number
  failed: number
  queued: number
  sent: number
  undelivered: number
  awaitingOptIn: number
  optInDeclined: number
  optInExpired: number
}

export type TextDeliveryCounts = Record<TextMessage["channel"], TextMessageDeliveryCounts> & {
  creditsUsed?: number
}

export type TextMessageFallbackCounts = {
  delivered: number
  deliveryUnknown: number
  failed: number
  queued: number
  sent: number
  undelivered: number
}

export type TextMessage = {
  id?: number
  externalId: string
  name: string
  body: string
  recipients: string[]
  guestCount: number
  sentAt: string | null
  createdAt: string
  updatedAt: string
  deliveryCounts: TextMessageDeliveryCounts
  fallbackCounts?: TextMessageFallbackCounts
  channel: "sms" | "whatsapp"
  smsFallback?: boolean
  whatsappContentSid?: string
  whatsappContentVariables?: Record<string, string>
  optedInRecipientCount?: number
  unoptedRecipientCount?: number
}

export type TextMessageTest = {
  to: string
  body?: string
  channel?: "sms" | "whatsapp"
  contentSid?: string
  contentVariables?: Record<string, string>
}

export type MergeTagReplacement = {
  mergeTag: string;
  value: string;
};

export type FormsTerminologyModalProps = {
  title: string
  event: DioboxEvent,
  onClose: () => void
  onSubmit: (values: any) => Promise<void>
  defaultTranslations: Translations
}

export type TextMessageReport = {
  guest: {
    id: string;
    name: string;
  }
  data: {
    to: string;
    sid: string;
    body: string;
    from: string;
    status: string;
    channel: "sms" | "whatsapp";
  }
  createdAt: string
  fallbackStatus: "sent" | "skipped_no_credits" | null
}

export type EmailReportRecipient = Transaction & {
  eventTitle: string
  personId: string
  emailId: number
  clicks: number
  opens: number
  softBounced: boolean
  hardBounced: boolean
  toEmail: string
  guest: {
    id: string
    status: number
    confirmedTime: string | null
    firstName: string
    lastName: string
    photoUrl: string
  }
}

export type URLSearchParam = {
  type: string
  value: string
}

export enum PreviewSelectOptions {
  FormSelectionPage = "Form Selection Page",
  GuestConfirmationPage = "Guest Confirmation Page",
  RegistrationForm = "Registration Form",
  TicketForm = "Ticket Form",
  RsvpForm = "RSVP Form",
  WaitlistForm = "Waitlist Form",
  SurveyForm = "Survey Form",
}
